import { createDateFormatter, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { memo, useCallback, useId, useMemo, useRef, useState } from "react";
import { ListFilter } from "lucide-react";
import { Select, ToggleChip } from "@nexus/ui";
import {
  defineLabels,
  resolveLabels,
  firstError,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  WidgetFooter,
  MetricValue,
  SeverityChip,
  StatusChip,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { JsonValue } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type { AnomalyEntry, SystemAnomaliesConfig, SystemAnomaliesData } from "./types";

export type {
  AnomalyEntry,
  AnomalyJournalEntry,
  SystemAnomaliesConfig,
  SystemAnomaliesData,
} from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No anomalies" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No anomalies detected for the selected scope.",
  },
  {
    key: "noMatchTitle",
    name: "No rows after filtering: title",
    default: "No matching anomalies",
  },
  {
    key: "noMatchDescription",
    name: "No rows after filtering: description",
    default: "Adjust the filters to widen the results.",
  },
  {
    key: "unresolvedHeading",
    name: "KPI caption: Unresolved anomalies",
    default: "Unresolved anomalies",
  },
  {
    key: "unresolvedOnly",
    name: "Toggle button: Unresolved only",
    default: "Unresolved only",
  },
  { key: "colSeverity", name: "Table column: Severity", default: "Severity" },
  { key: "colStatus", name: "Table column: Status", default: "Status" },
  { key: "colType", name: "Table column: Type", default: "Type" },
  { key: "colDomain", name: "Table column: Domain", default: "Domain" },
  { key: "colEvent", name: "Table column: Event", default: "Event" },
  {
    key: "colEngineTenant",
    name: "Table column: Engine / Tenant",
    default: "Engine / Tenant",
  },
  {
    key: "colDeviation",
    name: "Table column: Deviation",
    default: "Deviation",
  },
  { key: "colRaised", name: "Table column: Raised", default: "Raised" },
  { key: "filterType", name: "Filter caption: Type", default: "Type" },
  {
    key: "filterInfringement",
    name: "Filter caption: Infringement",
    default: "Infringement",
  },
  {
    key: "filterSeverity",
    name: "Filter caption: Severity",
    default: "Severity",
  },
  { key: "filterStatus", name: "Filter caption: Status", default: "Status" },
] as const);

export const manifest: WidgetManifest = {
  id: "system-anomalies",
  name: "System Anomalies",
  description: "Anomalies across the current scope, with quick triage filters and row drill-down.",
  version: "1.2.0",
  labels: LABELS,
  dataRequests: {
    anomalies: {
      source: "system-anomalies",
      contract: { name: "system-anomalies", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: SystemAnomaliesConfig = {
  title: "System Anomalies",
  interactionType: "anomaly.select",
};

const EMPTY_ANOMALIES: AnomalyEntry[] = [];

// ── Severity rank (for sort order) ──────────────────────────────────────────

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
};

function severityRank(severity: string): number {
  return SEVERITY_RANK[severity.toLowerCase()] ?? -1;
}

// ── Status helpers ───────────────────────────────────────────────────────────

const RESOLVED_STATUSES = new Set(["resolved", "closed", "cleared", "dismissed"]);

function isUnresolved(entry: AnomalyEntry): boolean {
  return !RESOLVED_STATUSES.has(entry.anomaly_status.toLowerCase());
}

function toStatusState(status: string): "ok" | "warn" | "error" | "neutral" {
  switch (status.toLowerCase()) {
    case "resolved":
    case "closed":
    case "cleared":
    case "dismissed":
      // Closed lifecycle → neutral (intentionally off, no alarm, no dot color).
      return "neutral";
    case "acknowledged":
    case "investigating":
      // A human has claimed the anomaly — being handled → amber △ warn.
      return "warn";
    // 'active' / 'open' / 'new' — no owner, unresolved, needs attention.
    // Maps to error (red ◇) to visually separate it from Acknowledged (amber △).
    // Note: status-error here signals "action required", NOT a system failure;
    // the severity chip on the same row already encodes criticality.
    case "active":
    case "open":
    case "new":
    default:
      return "error";
  }
}

// ── Formatting helpers ───────────────────────────────────────────────────────

function titleCase(value: string): string {
  if (!value) return "—";
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatAbsolute(value: string, timezone: TimezoneMode): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return createDateFormatter(timezone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatRelative(value: string): string {
  const date = new Date(value);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return value;

  const diffSec = Math.round((Date.now() - ms) / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.round(diffHour / 24);
  return `${diffDay}d ago`;
}

function formatDeviation(expected: number, actual: number): { label: string; up: boolean | null } {
  if (!Number.isFinite(expected) || expected === 0) {
    return { label: `${actual}`, up: null };
  }
  const pct = Math.round(((actual - expected) / Math.abs(expected)) * 100);
  const up = pct === 0 ? null : pct > 0;
  return { label: `${pct > 0 ? "+" : ""}${pct}%`, up };
}

// ── Local filter primitives ──────────────────────────────────────────────────

type LocalFilters = {
  anomaly_type: string;
  infringement_type: string;
  anomaly_severity: string;
  anomaly_status: string;
};

const EMPTY_FILTERS: LocalFilters = {
  anomaly_type: "",
  infringement_type: "",
  anomaly_severity: "",
  anomaly_status: "",
};

function distinctSorted(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function FilterSelect({
  label,
  emptyLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  emptyLabel: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-32 flex-col items-start gap-1">
      <span className="text-xs font-medium text-secondary">{label}</span>
      <Select
        label={label}
        controlSize="sm"
        value={value}
        emptyLabel={emptyLabel}
        options={options.map((option) => ({
          value: option,
          label: titleCase(option),
        }))}
        onValueChange={onChange}
        className={value ? "border-brand-cyan/40 text-primary" : "text-secondary"}
      />
    </div>
  );
}

function AnomalyFilterButton({
  filters,
  unresolvedOnly,
  expanded,
  controlsId,
  onExpandedChange,
}: {
  filters: LocalFilters;
  unresolvedOnly: boolean;
  expanded: boolean;
  controlsId: string;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const activeFilterCount =
    Number(unresolvedOnly) +
    Number(filters.anomaly_type !== "") +
    Number(filters.infringement_type !== "") +
    Number(filters.anomaly_severity !== "") +
    Number(filters.anomaly_status !== "");

  return (
    <button
      type="button"
      onClick={() => onExpandedChange(!expanded)}
      aria-expanded={expanded}
      aria-controls={controlsId}
      aria-label="Toggle filters"
      title="Filters"
      className={[
        "relative inline-flex h-7 w-7 items-center justify-center rounded text-muted transition-colors hover:bg-surface-elevated hover:text-primary",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
        expanded || activeFilterCount > 0 ? "bg-brand-cyan/10 text-brand-cyan" : "",
      ].join(" ")}
    >
      <ListFilter className="h-4 w-4" aria-hidden />
      {activeFilterCount > 0 && (
        <span className="absolute -right-1.5 -top-1.5 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-brand-cyan px-1 text-xs font-semibold leading-none text-white">
          {activeFilterCount}
        </span>
      )}
    </button>
  );
}

function AnomalyFilterRow({
  filters,
  options,
  unresolvedOnly,
  onFiltersChange,
  onClear,
  captions,
}: {
  filters: LocalFilters;
  options: Record<keyof LocalFilters, string[]>;
  unresolvedOnly: boolean;
  onFiltersChange: (filters: LocalFilters) => void;
  onClear: () => void;
  captions: {
    type: string;
    infringement: string;
    severity: string;
    status: string;
  };
}) {
  const hasActiveFilters = unresolvedOnly || Object.values(filters).some((value) => value !== "");

  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-2 border-y border-border bg-transparent px-3 py-2.5">
      <FilterSelect
        label={captions.type}
        emptyLabel="All types"
        value={filters.anomaly_type}
        options={options.anomaly_type}
        onChange={(value) => onFiltersChange({ ...filters, anomaly_type: value })}
      />
      <FilterSelect
        label={captions.infringement}
        emptyLabel="All infringements"
        value={filters.infringement_type}
        options={options.infringement_type}
        onChange={(value) => onFiltersChange({ ...filters, infringement_type: value })}
      />
      <FilterSelect
        label={captions.severity}
        emptyLabel="All severities"
        value={filters.anomaly_severity}
        options={options.anomaly_severity}
        onChange={(value) => onFiltersChange({ ...filters, anomaly_severity: value })}
      />
      <FilterSelect
        label={captions.status}
        emptyLabel="All statuses"
        value={filters.anomaly_status}
        options={options.anomaly_status}
        onChange={(value) => onFiltersChange({ ...filters, anomaly_status: value })}
      />
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="ml-auto h-7 px-1 text-xs font-medium text-muted transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ── Table cells ──────────────────────────────────────────────────────────────

const VALID_SEV_LEVELS = ["critical", "high", "medium", "low", "info"] as const;
type ValidSevLevel = (typeof VALID_SEV_LEVELS)[number];

function SeverityCell({ severity }: { severity: string }) {
  const level = severity.toLowerCase();
  const safeLevel = VALID_SEV_LEVELS.includes(level as ValidSevLevel)
    ? (level as ValidSevLevel)
    : null;

  if (!safeLevel) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded bg-border/40 px-1.5 py-[2px] text-xs font-semibold uppercase leading-none tracking-wide text-muted">
        —
      </span>
    );
  }
  return <SeverityChip level={safeLevel} />;
}

function StatusCell({ status }: { status: string }) {
  const state = toStatusState(status);
  return <StatusChip status={state} label={titleCase(status)} />;
}

/**
 * DeviationCell — Phase 4 fix.
 *
 * Deviation direction rule: color reflects anomaly_severity, not the arithmetic sign.
 * Rationale: all rows are anomalies by definition. A detection collapse of -80% at
 * Critical severity is equally dangerous as a +80% spike. The severity chip already
 * encodes urgency; DeviationCell shows magnitude and direction as semantic context
 * colored by severity:
 *   critical / high → status-error  (demands attention)
 *   medium          → status-warn   (caution)
 *   low / info / —  → muted         (informational only)
 */
function DeviationCell({ entry }: { entry: AnomalyEntry }) {
  const deviation = formatDeviation(entry.expected_value, entry.actual_value);
  const sev = entry.anomaly_severity.toLowerCase();
  const tone =
    sev === "critical" || sev === "high"
      ? "text-status-error-text"
      : sev === "medium"
        ? "text-status-warn-text"
        : "text-muted";

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium tabular-nums ${tone}`}
      title={`expected ${entry.expected_value} · actual ${entry.actual_value}`}
    >
      {deviation.up !== null && (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-3 w-3 shrink-0"
        >
          {deviation.up ? <path d="m6 15 6-6 6 6" /> : <path d="m6 9 6 6 6-6" />}
        </svg>
      )}
      {deviation.label}
    </span>
  );
}

const AnomalyRow = memo(function AnomalyRow({
  entry,
  interactive,
  isActive,
  onSelect,
}: {
  entry: AnomalyEntry;
  interactive: boolean;
  isActive: boolean;
  onSelect: (entry: AnomalyEntry) => void;
}) {
  const handleActivate = () => {
    if (interactive) onSelect(entry);
  };
  const timezone = useDisplayTimezone();

  return (
    <tr
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (interactive && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onSelect(entry);
        }
      }}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      aria-current={isActive ? "true" : undefined}
      aria-label={interactive ? `Open anomaly ${entry.anomaly_id}` : undefined}
      className={[
        "border-b border-border last:border-b-0 [&>td]:transition-colors [&>td]:duration-150",
        interactive ? "cursor-pointer" : "",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-cyan/45",
        isActive
          ? "[&>td]:bg-row-selected [&:hover>td]:bg-row-selected-hover"
          : "[&:hover>td]:bg-surface-elevated",
      ].join(" ")}
    >
      <td className="py-2 pl-3 pr-2">
        <SeverityCell severity={entry.anomaly_severity} />
      </td>
      <td className="px-2 py-2">
        <StatusCell status={entry.anomaly_status} />
      </td>
      <td className="max-w-45 px-2 py-2">
        <span className="block truncate font-medium text-primary" title={entry.anomaly_type}>
          {titleCase(entry.anomaly_type)}
        </span>
        <span className="block truncate text-xs text-muted" title={entry.infringement_type}>
          {titleCase(entry.infringement_type)}
        </span>
      </td>
      <td className="px-2 py-2 text-secondary">
        <span className="block truncate" title={entry.detection_domain}>
          {entry.detection_domain}
        </span>
      </td>
      <td className="max-w-40 px-2 py-2 text-secondary">
        <span className="block truncate" title={entry.event_label}>
          {entry.event_label}
        </span>
      </td>
      <td className="max-w-[9.375rem] px-2 py-2 text-secondary">
        <span className="block truncate" title={entry.engine_label}>
          {entry.engine_label}
        </span>
        <span className="block truncate text-xs text-muted" title={entry.tenant_label}>
          {entry.tenant_label}
        </span>
      </td>
      <td className="px-2 py-2 text-right">
        <DeviationCell entry={entry} />
      </td>
      <td
        className="whitespace-nowrap py-2 pl-2 pr-[1.625rem] text-right text-xs tabular-nums text-muted"
        title={formatAbsolute(entry.raised_at, timezone)}
      >
        {formatRelative(entry.raised_at)}
      </td>
    </tr>
  );
});

// ── Main component ───────────────────────────────────────────────────────────

const HEADER_KEYS = [
  "colSeverity",
  "colStatus",
  "colType",
  "colDomain",
  "colEvent",
  "colEngineTenant",
  "colDeviation",
  "colRaised",
] as const;

function SystemAnomaliesComponent({ data, config, dashboard }: WidgetProps<SystemAnomaliesConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["anomalies"]);
  const resolved = unwrap<SystemAnomaliesData>(data.anomalies);
  const isLoading = data.anomalies?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "System Anomalies";
  const interactionType =
    config.interactionType ?? defaultConfig.interactionType ?? "anomaly.select";
  const interactive = typeof dashboard.emitInteraction === "function";
  const activeAnomalyFilter = dashboard.filters.dataFilters.find(
    (filter) => filter.field === "anomaly_id",
  )?.value;
  const activeAnomalyId =
    typeof activeAnomalyFilter === "number"
      ? activeAnomalyFilter
      : typeof activeAnomalyFilter === "string" && activeAnomalyFilter !== ""
        ? Number(activeAnomalyFilter)
        : null;

  const anomalies = resolved?.anomalies ?? EMPTY_ANOMALIES;

  const [filters, setFilters] = useState<LocalFilters>(EMPTY_FILTERS);
  const [unresolvedOnly, setUnresolvedOnly] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterPanelId = useId();

  const options = useMemo(
    () => ({
      anomaly_type: distinctSorted(anomalies.map((a) => a.anomaly_type)),
      infringement_type: distinctSorted(anomalies.map((a) => a.infringement_type)),
      anomaly_severity: distinctSorted(anomalies.map((a) => a.anomaly_severity)),
      anomaly_status: distinctSorted(anomalies.map((a) => a.anomaly_status)),
    }),
    [anomalies],
  );

  const { rows, unresolvedCount } = useMemo(() => {
    const filtered = anomalies.filter((entry) => {
      if (filters.anomaly_type && entry.anomaly_type !== filters.anomaly_type) return false;
      if (filters.infringement_type && entry.infringement_type !== filters.infringement_type)
        return false;
      if (filters.anomaly_severity && entry.anomaly_severity !== filters.anomaly_severity)
        return false;
      if (filters.anomaly_status && entry.anomaly_status !== filters.anomaly_status) return false;
      if (unresolvedOnly && !isUnresolved(entry)) return false;
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const unresolvedDelta = Number(isUnresolved(b)) - Number(isUnresolved(a));
      if (unresolvedDelta !== 0) return unresolvedDelta;
      const severityDelta = severityRank(b.anomaly_severity) - severityRank(a.anomaly_severity);
      if (severityDelta !== 0) return severityDelta;
      return new Date(b.raised_at).getTime() - new Date(a.raised_at).getTime();
    });

    const unresolved = anomalies.reduce((count, entry) => count + (isUnresolved(entry) ? 1 : 0), 0);
    return { rows: sorted, unresolvedCount: unresolved };
  }, [anomalies, filters, unresolvedOnly]);

  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;
  const interactionTypeRef = useRef(interactionType);
  interactionTypeRef.current = interactionType;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const handleSelect = useCallback((entry: AnomalyEntry) => {
    dashboardRef.current.emitInteraction?.({
      type: interactionTypeRef.current,
      payload: {
        anomaly_id: entry.anomaly_id,
        anomaly_type: entry.anomaly_type,
        infringement_type: entry.infringement_type,
        tenant_id: entry.tenant_id,
        event_id: entry.event_id,
        detection_domain: entry.detection_domain,
        anomaly_severity: entry.anomaly_severity,
        anomaly_status: entry.anomaly_status,
        visible_anomalies: rowsRef.current.map((row) => ({
          ...row,
        })) as unknown as JsonValue,
      } as unknown as JsonValue,
      source: { dataKey: "anomalies" },
    });
  }, []);

  if (error) {
    console.error("[SystemAnomaliesWidget] Failed to load data:", error);

    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (anomalies.length === 0 && isLoading) {
    return (
      <Widget title={title}>
        <div className="space-y-2 p-3.5">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="h-4 w-16 rounded skeleton" />
              <div className="h-4 w-20 rounded skeleton" />
              <div className="h-4 flex-1 rounded skeleton" />
              <div className="h-4 w-16 rounded skeleton" />
            </div>
          ))}
        </div>
      </Widget>
    );
  }

  if (anomalies.length === 0) {
    return (
      <Widget title={title}>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget
      title={title}
      actions={
        <div className="flex items-center gap-2">
          {isLoading && <UpdatingPill />}
          <AnomalyFilterButton
            filters={filters}
            unresolvedOnly={unresolvedOnly}
            expanded={filtersExpanded}
            controlsId={filterPanelId}
            onExpandedChange={setFiltersExpanded}
          />
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-4 py-4">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="mb-1 text-metric-label text-secondary">{labels.unresolvedHeading}</p>
              <MetricValue state={unresolvedCount ? "error" : "neutral"}>
                {unresolvedCount}
              </MetricValue>
            </div>
            <p className="text-metric-caption text-muted">
              {new Set(anomalies.filter(isUnresolved).map((entry) => entry.event_id)).size} events
              affected
              <br />
              {
                anomalies.filter(
                  (entry) =>
                    isUnresolved(entry) &&
                    severityRank(entry.anomaly_severity) >= severityRank("high"),
                ).length
              }{" "}
              high or critical
            </p>
          </div>
          <ToggleChip pressed={unresolvedOnly} onPressedChange={setUnresolvedOnly}>
            {labels.unresolvedOnly}
          </ToggleChip>
        </div>
        {filtersExpanded && (
          <div id={filterPanelId}>
            <AnomalyFilterRow
              filters={filters}
              options={options}
              unresolvedOnly={unresolvedOnly}
              onFiltersChange={setFilters}
              captions={{
                type: labels.filterType,
                infringement: labels.filterInfringement,
                severity: labels.filterSeverity,
                status: labels.filterStatus,
              }}
              onClear={() => {
                setFilters(EMPTY_FILTERS);
                setUnresolvedOnly(false);
              }}
            />
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto">
          {rows.length === 0 ? (
            <WidgetState title={labels.noMatchTitle} description={labels.noMatchDescription} />
          ) : (
            <table className="w-full min-w-[65rem] table-fixed border-collapse text-left text-sm">
              <colgroup>
                <col className="w-24" />
                <col className="w-32" />
                <col className="w-[18%]" />
                <col className="w-[12%]" />
                <col className="w-[17%]" />
                <col className="w-[19%]" />
                <col className="w-24" />
                <col className="w-28" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-surface-elevated">
                <tr className="border-b border-border">
                  {HEADER_KEYS.map((headerKey, index) => (
                    <th
                      key={headerKey}
                      className={[
                        "whitespace-nowrap py-3 text-xs font-medium text-muted",
                        index === 0 ? "pl-3 pr-2" : "px-2",
                        headerKey === "colDeviation" || headerKey === "colRaised"
                          ? "text-right"
                          : "",
                        headerKey === "colRaised" ? "pr-[1.625rem]" : "",
                      ].join(" ")}
                    >
                      {labels[headerKey]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <AnomalyRow
                    key={entry.anomaly_id}
                    entry={entry}
                    interactive={interactive}
                    isActive={activeAnomalyId === entry.anomaly_id}
                    onSelect={handleSelect}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
        <WidgetFooter>
          <span>{rows.length} anomalies shown</span>
          {unresolvedCount > 0 && <span>· {unresolvedCount} unresolved in scope</span>}
        </WidgetFooter>
      </div>
    </Widget>
  );
}

export const Component = SystemAnomaliesComponent;

const widgetModule: WidgetModule<SystemAnomaliesConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
